/**
 * Turns an extracted portfolio into the grid the Excel widget renders.
 *
 * Columns start at A: Symbol, Trade Date, Cost Basis, Quantity, Direction,
 * Last Price. The first five come straight from the image; Last Price is a
 * DSL cell whose expression we already know, so it ships with `resolvedDsl`
 * filled in and goes straight to the Rust engine — no DSL agent call.
 */

import { MAX_EXCEL_ROWS, type ExcelCell } from './excelConfig';
import type { PortfolioSnapshot } from '../../../services/PortfolioImageService';

export const PORTFOLIO_HEADERS: readonly string[] = [
  'Symbol',
  'Trade Date',
  'Cost Basis',
  'Quantity',
  'Direction',
  'Last Price',
];

/** One row goes to the header, so the sheet holds this many positions. */
export const MAX_PORTFOLIO_POSITIONS = MAX_EXCEL_ROWS - 1;

const literal = (value: string | number | null): ExcelCell => ({
  raw: value === null ? '' : String(value),
});

/** Last price for one symbol. Same expression the DSL agent would produce. */
const lastPriceDsl = (symbol: string): string => `stock("${symbol}")`;

const lastPriceCell = (symbol: string): ExcelCell => ({
  raw: `=${lastPriceDsl(symbol)}`,
  resolvedDsl: lastPriceDsl(symbol),
});

export function portfolioSheetCells(snapshot: PortfolioSnapshot): ExcelCell[][] {
  return [
    PORTFOLIO_HEADERS.map((header) => literal(header)),
    ...snapshot.positions.map((position) => [
      literal(position.symbol),
      literal(position.trade_date),
      literal(position.cost_basis),
      literal(position.quantity),
      literal(position.side),
      lastPriceCell(position.symbol),
    ]),
  ];
}
